import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	QueryList,
	SimpleChanges,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { BlindListComponent } from '@features/blind-list/blind-list.component';
import { ShareService } from '@core/services/share-data/share-data.service';
import { EngineService } from '@core/services/engine/engine.service';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { ActivatedRoute } from '@angular/router';
import { ScreenshotService } from '@core/services/screenshot/screenshot.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { ScreenshotToBlindService } from '@core/services/screenshot-to-blind/screenshot-to-blind.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { VariantService } from '@core/services/variant/variant.service';
import { BlindTypes, SectionKey, SectionKeysEnum } from '@root/app.interfaces';
import { filter, firstValueFrom, iif, of, take } from 'rxjs';
import { SizeChangeComponent } from '@features/size-change/size-change.component';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { PartAvailabilityService } from '@core/services/part-availability/part-availability.service';
import { ToolBarComponent } from '@features/tool-bar/tool-bar.component';
import { IS_IOS, SELECTORS } from '@root/app.config';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { AccordionSectionService } from '@features/blind-list/services/accordion-section.service';
import { ResetAppService } from '@core/services/reset-app/reset-app.service';

@Component({
	selector: 'app-outdoor-list',
	templateUrl: './outdoor-list.component.html',
	styleUrls: ['../blind-list.component.scss', '../blind-list.responsive.scss'],
	providers: [AccordionSectionService],
})
export class OutdoorListComponent extends BlindListComponent implements OnInit, AfterViewInit, OnChanges {
	constructor(
		public shareDataService: ShareService,
		public engineService: EngineService,
		public resetAppService: ResetAppService,
		public serverDataService: ServerDataService,
		public saveSessionBlind: SaveSessionBlindsService,
		public route: ActivatedRoute,
		public screenShotService: ScreenshotService,
		public screenshotToBlindService: ScreenshotToBlindService,
		public sessionStorageService: SessionStorageService,
		public passDataService: PassDataService,
		public changeDetection: ChangeDetectorRef,
		public blindStateService: BlindStateService,
		public partAvailabilityService: PartAvailabilityService,
		public variant: VariantService,
		private translateService: TranslateService,
		public toolBarComponent: ToolBarComponent,
		public setupPageService: SetupPageService,
		public accordionSectionService: AccordionSectionService<SectionKeysEnum>,
	) {
		super(
			shareDataService,
			engineService,
			resetAppService,
			serverDataService,
			saveSessionBlind,
			route,
			screenShotService,
			screenshotToBlindService,
			sessionStorageService,
			passDataService,
			changeDetection,
			blindStateService,
			partAvailabilityService,
			variant,
			toolBarComponent,
			setupPageService,
			accordionSectionService,
		);
	}

	@Input() index: number;
	@Input() blindsListComponent: BlindListComponent;

	@Output() setupData = new EventEmitter();

	@ViewChild('sizeChange') sizeChange!: SizeChangeComponent;

	@ViewChildren('cfgHeader') private headers!: QueryList<ElementRef<HTMLElement>>;
	@ViewChildren('disabledSectionPopover') disabledSectionPopovers!: QueryList<NgbPopover>;

	blind: any;
	isShop = this.variant.isShop();
	protected sectionDisabled: Record<Exclude<SectionKeysEnum, SectionKeysEnum.ColorOfFixtures>, boolean> = {
		material: false,
		size: this.isShop,
		'frame-color': this.isShop,
		'top-style': this.isShop,
		'bottom-bar': this.isShop,
		operation: this.isShop,
		mounting: this.isShop,
	};
	private popoverTimeout: ReturnType<typeof setTimeout>;
	private activePopover: NgbPopover | null = null;
	public isTouchMoving = false;

	protected readonly sectionsWithoutScroll: SectionKey[] = this.blindStateService.getSectionWithoutScroll(BlindTypes.Outdoor);

	ngOnInit(): void {
		super.ngOnInit();
		this.blind = this.blindsData[this.index];

		if (this.variant.isShop()) {
			const missingSections = this.blindStateService.missingSections(this.blind.blind_id);
			if (missingSections.length === 0) {
				// Based on the assumption all section have to be complete to get to the overview screen
				Object.keys(this.sectionDisabled).forEach((key) => {
					this.sectionDisabled[key] = false;
				});
			} else if (missingSections.length < Object.keys(this.sectionDisabled).length) {
				// When opening a save link where some sections are done but not all
				Object.keys(this.sectionDisabled).forEach((key) => {
					this.sectionDisabled[key] = false;
				});

				for (let i = 1; i < missingSections.length; i++) {
					this.sectionDisabled[missingSections[i]] = true;
				}
			}
		}

		this.openSectionHandler();
		this.closeSectionHandler();

		setTimeout(this.blindNameSave.bind(this, this.blind.blind_id));
		this.getModelLoadedStatus.unsubscribe();
		this.getScreenShot.unsubscribe();
		this.getBlindsData.unsubscribe();
		this.getBlindDelete.unsubscribe();
		this.getSelectedModel.unsubscribe();
		this.getAccordionMobileBlindList.unsubscribe();
		this.getViewType.unsubscribe();
		this.getAccordionBlindSubscription.unsubscribe();
		this.blindsAvailabilitySubs?.unsubscribe();
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
		this.registerAccordionSections();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.index.firstChange) {
			this.blind = this.blindsListComponent.blindsData[this.index];
			this.blindsData = this.blindsListComponent.blindsData;
		}
	}

	blindNameSave(id: any): void {
		super.blindNameSave(id);
		if (this.blindsListComponent.blindsData) {
			this.blindsListComponent.blindsData[this.index].name = this.blind.name;
		}
	}

	onBlindItemDescriptionHandler<T>(event: T, id?: number): void {
		super.onBlindItemDescriptionHandler(event, id);
		this.setupData.emit(event);

		if (event['size'] && event['size'].configured) {
			this.sectionDisabled['size'] = false;
		}
	}

	async openSection(section: SectionKey, closeSections: boolean = false): Promise<void> {
		this.sectionDisabled[section] = false;
		const header = this.getHeaderElement(section);

		if (!header) {
			return;
		}

		this.closeDisabledSectionPopovers();

		const selector = SELECTORS.config_item;
		const sectionContainer = header.closest(selector) as HTMLElement;

		if (!this.isSectionOpen(section)) {
			await this.accordionSection(section, true, closeSections);

			if (!this.sectionsWithoutScroll.includes(section)) {
				sectionContainer.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}

	closeSection(section: SectionKey) {
		this.sectionDisabled[section] = true;
		const header = this.getHeaderElement(section);

		if (!header) {
			return;
		}

		if (this.isSectionOpen(section)) {
			void this.accordionSection(section, false);
		}
	}

	private getHeaderElement(section: SectionKey): HTMLElement | null {
		return this.headers.find((header) => header.nativeElement.dataset.section === section)?.nativeElement ?? null;
	}

	onHeaderTouchEnd(event: TouchEvent, section: SectionKey) {
		if (this.isTouchMoving) {
			this.isTouchMoving = false;
			return;
		}

		if (!this.sectionDisabled[section]) {
			return;
		}

		event.stopPropagation();
		this.showDisabledPopover(this.getDisabledSectionPopover(section));
		this.isTouchMoving = false;
	}

	onHeaderTouchMove(): void {
		this.isTouchMoving = true;
	}

	onHeaderHover(entering: boolean, section: SectionKey, event: PointerEvent) {
		if (!this.isRealMouseHover(event)) {
			return;
		}

		if (!this.sectionDisabled[section]) {
			return;
		}

		const popover = this.getDisabledSectionPopover(section);

		if (entering) {
			const notSelectedSectionKey = this.blindStateService.missingSections(this.blind.blind_id)[0];
			popover.ngbPopover = this.translateService.instant(`taskbar.items.section_disabled.${notSelectedSectionKey}`);
			popover.open();
			this.activePopover = popover;
		} else {
			popover.close();
			if (this.activePopover === popover) {
				this.activePopover = null;
			}
		}
	}

	private isRealMouseHover(event: PointerEvent): boolean {
		return event.pointerType === 'mouse' && !IS_IOS();
	}

	private getDisabledSectionPopover(section: SectionKey): NgbPopover {
		return this.disabledSectionPopovers.find((popover) => popover.popoverContext.section === section);
	}

	private closeDisabledSectionPopovers(): void {
		this.disabledSectionPopovers.forEach((popover) => popover.close());
		this.activePopover = null;
	}

	private showDisabledPopover(popover: NgbPopover) {
		if (this.activePopover && this.activePopover !== popover) {
			this.activePopover.close();
		}

		if (this.popoverTimeout) {
			clearTimeout(this.popoverTimeout);
			this.popoverTimeout = null;
		}

		if (!popover.isOpen()) {
			const notSelectedSectionKey = this.blindStateService.missingSections(this.blind.blind_id)[0];
			popover.ngbPopover = this.translateService.instant(`taskbar.items.section_disabled.${notSelectedSectionKey}`);
			popover.open();
		}

		this.activePopover = popover;

		const target = popover;
		this.popoverTimeout = setTimeout(() => {
			if (this.activePopover === target) {
				target.close();
				this.activePopover = null;
			}
			this.popoverTimeout = null;
		}, 2500);
	}

	onWrapperLeave(event: PointerEvent) {
		if (event.pointerType === 'mouse' && this.variant.isShop()) {
			this.sizeChange.mouseLeave();
		}
	}

	openSectionHandler(): void {
		this.getOpenSection = this.shareDataService.getOpenSection.subscribe(async (res) => {
			if (res.blindId === this.blind.blind_id) {
				if (res.openToolBar) {
					await this.openToolBar();
				}

				const blindElement = document.querySelector(`${this.blindSelectors.item}[id="${res.blindId}"]`) as HTMLElement;
				const isBlindOpen = blindElement?.dataset?.slided === 'true';
				const isCurrentBlind = res.blindId === this.blindsListComponent.selectedBlindId;

				if (!isCurrentBlind || (isCurrentBlind && !isBlindOpen)) {
					this.shareDataService.getAccordionFinished
						.pipe(
							filter((id) => id === res.blindId),
							take(1),
						)
						.subscribe(() => this.openSection(res.section, true));

					this.blindsListComponent.accordionBlindItems(null, res.blindId, this.blind.type);
				} else {
					void this.openSection(res.section);
				}
			}
		});
	}

	closeSectionHandler(): void {
		this.getCloseSection = this.shareDataService.getCloseSection.subscribe((res) => {
			if (res.blindId === this.blind.blind_id) {
				this.closeSection(res.section);
			}
		});
	}

	async openToolBar(): Promise<void> {
		const needToOpenDesktopToolBar = this.blindsListComponent.isToolBarCollapsed && this.toolBarComponent.simpleBarStatus;
		const needToOpenMobileToolBar = this.blindsListComponent.mobileBlindListStatus() === false;

		if (needToOpenDesktopToolBar) {
			this.shareDataService.setCollapseToolBar(false);
		}

		if (needToOpenMobileToolBar) {
			this.blindsListComponent.callBlindListSwipeAnimation(true);
		}

		if (!needToOpenDesktopToolBar && !needToOpenMobileToolBar) {
			return firstValueFrom(of(null));
		}

		await firstValueFrom(
			iif(() => needToOpenDesktopToolBar, this.blindsListComponent.tollBarCollapse, this.shareDataService.getAccordionFinished).pipe(
				take(1),
			),
		);
	}
}
